import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const cropMediaSkill: Skill = {
    name: SKILL_NAMES.cropMedia,
    description:
        'Load BEFORE adjusting an existing clip: framing (subject cut off, bad crop, needs zoom or motion, too bright under text) or playback (a short clip loops, wrong trim, too fast/slow).',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Adjusting Media (framing & playback)

Every adjustment to a clip that is already in the slot goes through \`${TOOL_NAMES.editMedia}\`.
To put a *different* clip in the slot, use \`${TOOL_NAMES.setMedia}\` — that is all it does.

A media asset's frame is fixed by the design — you reframe the media *within* it
using \`${TOOL_NAMES.editMedia}\` (scale, position, Ken Burns, brightness).

## How positioning works
- Position translates the media relative to the center of its frame, bounded by the
  asset's \`mediaXTranslationRange\` and \`mediaYTranslationRange\` in the video state.
  Read the ranges first — a position outside them does nothing.
- **The axes are inverted from what you'd expect**: to move the media LEFT pass a
  positive x, RIGHT a negative x; UP a positive y, DOWN a negative y. (Think of it
  as moving the frame's window over the media, not the media itself.) So
  \`{ x: 10 }\` shifts it left, \`{ x: -10 }\` right, \`{ y: 15 }\` up, \`{ y: -15 }\` down.
- \`scale\` is a percentage: above 100 enlarges the media (width and height), below
  100 shrinks it. Scaling up zooms in and widens the translation ranges — if the
  range is too narrow to reach the crop you want, increase the scale first.

## The reframe loop
1. Read the asset's current scale, translation, and ranges from the video state.
2. Apply \`${TOOL_NAMES.editMedia}\` with the new scale/position.
3. Re-render the scene (\`${TOOL_NAMES.imageAsk}\`, optionally passing the media id too so the
   model can reason about how the media sits in its frame) and check the result.
4. Iterate until the composition lands.

## What good framing means
- The subject is well-composed: no cut-off faces or key objects, especially at the
  frame edges.
- Nothing important sits under text, captions, or overlaid assets — reposition the
  media (or dim it with \`brightness\` when it's a backdrop for text) rather than
  accepting a collision.
- **Ken Burns** (\`zoom_in\`, \`zoom_out\`, \`pan_*\`) adds motion to still images —
  useful when a photo has to carry a scene; \`none\` disables it.

## Playback (video clips only)
Read \`trimStartMs\`, \`trimEndMs\`, \`sourceDurationMs\`, \`playbackMode\` and \`playbackRate\`
from the video state to see how a clip is playing, then adjust with \`${TOOL_NAMES.editMedia}\`:
- **A short clip that repeats or freezes**: a clip shorter than its scene either loops
  (\`playbackMode: 'loop'\`) or holds its last frame (\`playbackMode: 'hold'\`). If a loop
  reads as a jarring restart, switch to \`hold\`; if a frozen frame looks broken, switch to
  \`loop\`. Better still, give the scene a clip that is long enough.
- **Trim** with \`start_time\`/\`end_time\` (milliseconds into the source clip) to keep only
  the moment you want. On a text-on-media scene the scene duration follows the trim, so
  trimming also shortens the scene.
- **Speed** with \`playback_rate\` (1 = normal, below 1 slower, above 1 faster).
- These apply to video only — they are ignored on images. To trim a voiceover or
  talking-head recording, use \`${TOOL_NAMES.trimSceneGroup}\` instead.`;
    },
};
